import React, {useLayoutEffect, useState} from 'react';
import {SegmentedControl, Tooltip, tokensForThemeProvider} from '@forecast-it/design-system';
import Styled from 'styled-components';

export type StageBandBarItem = {
	/** Stable identity for the segment, and the value emitted on selection. */
	value: string;
	/**
	 * The first line of the segment — a single percentage on the settings preview, a range in the win
	 * chance picker. The component does not care which; it only stacks it above the label.
	 */
	topText: string;
	/** The band's label, shown on the second line. */
	label: string;
	dataCy?: string;
};

type Props = {
	items: StageBandBarItem[];
	/** The selected segment's value. Pass '' for a read-only bar with nothing highlighted. */
	value: string;
	/** Omit for a read-only bar. Re-clicking a segment emits '' and is ignored. */
	onChange?: (value: string) => void;
	dataCy?: string;
};

// The DS SegmentedControl item is a fixed 44px single-line control; these two-line band items are
// taller, so we let each item grow (height: auto) and shrink to share the row evenly (min-width: 0
// overrides the flex item's default min-content width so all bands fit without overflowing). The
// wrapper class + element selector out-specifies the DS item's own single-class rule.
const BandControl = Styled.div<{$readOnly: boolean}>`
	width: 100%;
	& button {
		height: auto;
		min-width: 0;
	}
	/* A read-only bar holds no selection, so no segment ever takes the control's white "checked" look
	   and the whole bar reads flat grey. Hover gets that look instead, from the control's own checked
	   tokens. The doubled wrapper class is needed to out-specify the control's own
	   \`&:hover:not(:disabled)\` rule, and border-color is set rather than a shadow because the button
	   already reserves its border width with a transparent colour, so nothing reflows. */
	${props =>
		props.$readOnly
			? `&& button:hover:not(:disabled) {
					background-color: ${tokensForThemeProvider.sky.sys.color.background.neutral['1']};
					border-color: ${tokensForThemeProvider.sky.sys.color.border.enabled};
				}`
			: ''}
`;
const ItemBody = Styled.span`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	width: 100%;
	/* min-width lets the body shrink below its text's natural width, which is what allows the label to be
	   cut off rather than pushing the segment wider than its share of the bar. */
	min-width: 0;
	text-align: center;
`;
const TopText = Styled.span`
	font-weight: 500;
`;
// A label longer than its segment is cut off rather than wrapping or widening the segment: the bar is one
// row, and a set of ten stages leaves each of them narrow. Hovering the label shows the whole name in a
// tooltip above it; assistive technology reads it from the segment's own accessible name.
const StageName = Styled.span`
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 400;
`;
// A stage label is account data, so it can be one long unbroken run of characters — and the tooltip that
// exists to show a cut-off label in full is exactly where such a label arrives. The tooltip caps its own
// width but wraps only at spaces, so a label with none of them runs straight out through the side of the
// box. Breaking mid-word is what keeps it inside.
const WrappingLabel = Styled.span`
	overflow-wrap: anywhere;
`;

/**
 * A segment's label, with the tooltip attached only while the label is actually cut off.
 *
 * A tooltip that repeats a label already fully readable is noise — and worse, it covers the neighbouring
 * segments to say nothing new. Whether the label is cut off is not a property of the text alone: the same
 * name fits at five stages and not at ten, and it changes again as the admin types or the window resizes.
 * So it is measured from the rendered element rather than guessed from a character count, and re-measured
 * whenever the label or the segment's width changes.
 */
const BandLabel = ({label}: {label: string}): JSX.Element => {
	const [node, setNode] = useState<HTMLSpanElement | null>(null);
	const [isCutOff, setIsCutOff] = useState(false);

	useLayoutEffect(() => {
		if (!node) {
			return;
		}
		// Sub-pixel text widths make scrollWidth round a hair over clientWidth on a label that is in fact
		// whole, which would attach a tooltip to every segment; a pixel of slack is what stops that.
		const measure = () => setIsCutOff(node.scrollWidth > node.clientWidth + 1);
		measure();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		// Measured on the frame after the observation rather than inside it. Measuring inline re-renders
		// this label while the browser is still delivering that same round of resizes, which it reports as
		// "ResizeObserver loop completed with undelivered notifications" — harmless in itself, but the dev
		// server's overlay raises it as a full-screen error. Deferring a frame lets the round finish first.
		let frame = 0;
		const observer = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measure);
		});
		observer.observe(node);
		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [node, label]);

	const name = <StageName ref={setNode}>{label}</StageName>;
	return isCutOff ? (
		<Tooltip content={<WrappingLabel>{label}</WrappingLabel>} width={Tooltip.WIDTH.s}>
			{name}
		</Tooltip>
	) : (
		name
	);
};

/**
 * Equal-width segments stacking a value above a label. Used both as an interactive
 * picker and — by passing an empty value and no onChange — as a read-only preview of a band set.
 */
export const StageBandBar = ({items, value, onChange, dataCy}: Props): JSX.Element => (
	<BandControl $readOnly={!onChange}>
		<SegmentedControl
			value={value}
			onValueChange={(next: string) => {
				// Radix single ToggleGroup deselects on re-click (emits ''); ignore empty so a value is always held.
				if (next && onChange) {
					onChange(next);
				}
			}}
			data-cy={dataCy}
		>
			{items.map(item => (
				<SegmentedControl.Item
					key={item.value}
					value={item.value}
					label={`${item.topText} ${item.label}`}
					data-cy={item.dataCy}
				>
					<ItemBody>
						<TopText>{item.topText}</TopText>
						{/* The tooltip anchors on the label and opens centred above it, rather than trailing the
							    cursor the way the browser's own title tooltip does. */}
						<BandLabel label={item.label} />
					</ItemBody>
				</SegmentedControl.Item>
			))}
		</SegmentedControl>
	</BandControl>
);

export default StageBandBar;
